import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePowerBiContext } from '../../hooks/usePowerBiContext';
import { useRedirectToFirstApp } from '../../hooks/useRedirectToFirstApp';
import { NoAccessMessage } from '../no-access-message/no-access-message';
import appStyles from '../../app.module.scss';
import pageStyles from './power-bi-page.module.scss';
import { EmbedType, PowerBIEmbed } from 'powerbi-client-react';
import { models, Report } from 'powerbi-client';
import { useSetAtom } from 'jotai';
import { pageTitleAtom } from '../../atoms/pageTitleAtom';
import { PowerBiToolbar } from '../power-bi-toolbar/power-bi-toolbar';
import { useUserViews } from '../../hooks/useUserViews';
import { useAddUserView } from '../../hooks/useAddUserView';
import { useDeleteUserViews } from '../../hooks/useDeleteUserViews';
import { useUpdateUserView } from '../../hooks/useUpdateUserView';
import { captureBookmarkState } from '../../utils/captureBookmarkState';
import { LoadingSpinner } from '@spins-gcp/shared-components-react';

const LOADER_DEBOUNCE_MS = 2000;
const LOADER_MIN_DISPLAY_MS = 1500;

interface PbiPageProps {
  title: string;
  children: React.ReactNode;
}

function PowerBiPage({ title, children: _children }: PbiPageProps) {
  const setPageTitle = useSetAtom(pageTitleAtom);

  useEffect(() => {
    setPageTitle(title);
  }, [setPageTitle, title]);

  const powerBiContext = usePowerBiContext();
  const track = powerBiContext.track;
  const accessDenied = powerBiContext.hasAccess === false;

  const prevHadAccess = useRef(false);
  useEffect(() => {
    if (powerBiContext.hasAccess === true) {
      prevHadAccess.current = true;
    }
  }, [powerBiContext.hasAccess]);

  const isMimicSwitch = accessDenied && prevHadAccess.current;
  const { isRedirecting } = useRedirectToFirstApp(isMimicSwitch);

  const reportRef = useRef<Report | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [showApplyLoader, setShowApplyLoader] = useState(false);
  const [embedReady, setEmbedReady] = useState(false);
  const { views, loading: viewsLoading } = useUserViews(title);

  const { addUserView } = useAddUserView();
  const { deleteUserViews } = useDeleteUserViews();
  const { updateUserView } = useUpdateUserView();

  const handleEmbeddedComponent = useCallback(
    (embed: Report) => {
      reportRef.current = embed;
      setReport(embed);
      setEmbedReady(false);
      // Register directly on the embed object so the handler survives re-embeds
      // (powerbi-client-react's eventHandlers prop skips re-registration when the
      // handler string is unchanged, causing missed events on new embed instances).
      embed.off('loaded');
      embed.on('loaded', () => setEmbedReady(true));
      embed.off('rendered');
      embed.on('rendered', () => setEmbedReady(true));

      embed.off('pageChanged');
      embed.on('pageChanged', (event) => {
        const page = (event as { detail: { newPage: { displayName: string } } })
          ?.detail?.newPage?.displayName;
        track?.('report_page_changed', { reportName: title, page });
      });
    },
    [title, track]
  );

  const handleCaptureView = useCallback(
    async (viewName: string) => {
      const base64Embedding = await captureBookmarkState(reportRef.current);
      if (base64Embedding === null) return;

      await addUserView({
        variables: { viewInput: { viewName, base64Embedding, appName: title } },
      });
    },
    [addUserView, title]
  );

  const handleDeleteView = useCallback(
    async (viewId: string) => {
      await deleteUserViews({
        variables: { viewIds: [viewId] },
      });
    },
    [deleteUserViews]
  );

  const handleSaveView = useCallback(
    async (viewId: string, viewName?: string) => {
      const base64Embedding = await captureBookmarkState(reportRef.current);
      if (base64Embedding === null) return;

      await updateUserView({
        variables: {
          viewId,
          viewInput: { viewName, base64Embedding, appName: title },
        },
      });
    },
    [updateUserView, title]
  );

  const handleResetReport = useCallback(async () => {
    if (!reportRef.current) return;
    setShowApplyLoader(true);
    try {
      await Promise.all([
        reportRef.current.reload(),
        new Promise((resolve) => setTimeout(resolve, LOADER_MIN_DISPLAY_MS)),
      ]);
    } finally {
      setShowApplyLoader(false);
    }
  }, []);

  const handleApplyView = useCallback(async (base64Embedding: string) => {
    if (!reportRef.current || !base64Embedding) return;
    const timer = setTimeout(
      () => setShowApplyLoader(true),
      LOADER_DEBOUNCE_MS
    );
    try {
      await reportRef.current.bookmarksManager.applyState(base64Embedding);
    } catch {
      // SDK errors (e.g. embed not yet interactive)
      // still marks the view as active so the user can rename/delete it.
    } finally {
      clearTimeout(timer);
      setShowApplyLoader(false);
    }
  }, []);

  if (powerBiContext.loading || (isMimicSwitch && isRedirecting)) {
    return <LoadingSpinner />;
  }

  if (accessDenied) {
    return (
      <NoAccessMessage message="You don't have access to this application." />
    );
  }

  return (
    <>
      <PowerBiToolbar
        appName={powerBiContext.appName ?? title}
        report={report}
        views={views}
        viewsLoading={viewsLoading}
        embedReady={embedReady}
        onCaptureView={handleCaptureView}
        onApplyView={handleApplyView}
        onDeleteView={handleDeleteView}
        onDeleteSuccess={handleResetReport}
        onExitView={handleResetReport}
        onUpdateView={handleSaveView}
      />
      <div
        className={pageStyles['embed-container']}
        data-embed-status={embedReady ? 'ready' : 'loading'}
      >
        {showApplyLoader && (
          <div className={pageStyles['embed-overlay']}>
            <LoadingSpinner />
          </div>
        )}
        <PowerBIEmbed
          embedConfig={{
            type: EmbedType.Report,
            id: powerBiContext.config?.reportId,
            embedUrl: powerBiContext.config?.embedUrl,
            accessToken: powerBiContext.config?.embedToken,
            tokenType: models.TokenType.Embed,
            settings: {
              background: models.BackgroundType.Default,
              panes: {
                pageNavigation: {
                  visible: true,
                  position: 1,
                },
              },
            },
          }}
          getEmbeddedComponent={(embed) =>
            handleEmbeddedComponent(embed as Report)
          }
          cssClassName={appStyles['powerbi-embed-wrapper']}
        />
      </div>
    </>
  );
}

export default PowerBiPage;
